import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import {
    asArrayValue,
    asBoolean,
    asDate,
    asObjectValue,
    asString,
    isLoadedDefined,
    loadedDefinedMap,
} from "common/components/component-helpers";
import type { ForceValueProducer, Value, ValueProducer } from "common/components/types";
import { ActionSource, asUI, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { defined, mapFilterUndefined } from "common/Support";
import { getNowTimeZoneAgnostic } from "date-time/support";
import { useForce } from "hooks/use-force";
import { useNetworkStatus } from "hooks/use-network-status";
import type { OnClickHandlerWithCustomContext } from "hooks/use-on-click-from-action";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import EmptyWrapper from "webapp/ui/views/empty-wrapper/empty-wrapper";
import SwipeStack from "webapp/ui/views/swipe-stack/swipe-stack";

interface Item {
    readonly swipedTimestamp: Date;
    readonly swipedTimestampVP: ValueProducer | undefined;
    readonly key: string;
    readonly isEnabled: boolean;
    readonly data: ValueProducer;
}

function deconstructItems(list: Value, force: ForceValueProducer): readonly Item[] {
    return mapFilterUndefined(asArrayValue(list), vp => {
        const itemValue = force(vp);
        if (!isLoadedDefined(itemValue)) return undefined;
        const {
            data: dataVP,
            swipedTimestamp: swipedTimestampVP,
            key: keyVP,
            isEnabled: isEnabledVP,
        } = asObjectValue(itemValue);
        const swipedTimestampValue = definedMap(swipedTimestampVP, force);
        const swipedTimestamp = asDate(swipedTimestampValue) ?? new Date(2018, 8, 5);
        const keyValue = force(keyVP);
        if (swipedTimestampValue === LOADING || keyValue === LOADING) return undefined;
        const isEnabledValue = force(isEnabledVP);
        const isEnabled = isEnabledValue === LOADING ? false : definedMap(isEnabledValue, asBoolean) ?? true;
        return { swipedTimestamp, swipedTimestampVP, key: asString(keyValue), isEnabled, data: dataVP };
    });
}

export const TinderMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[listValue, contents, onLeftValue, onRightValue, emptyMessageValue], [, , , , , rowToSetVP]] =
        useForceValueProducers(getValueProducers, force, ctx);
    const { onClickWithCustomContext: onClickLeft } = useOnClickFromAction(onLeftValue, ActionSource.Tinder, ctx, env);
    const { onClickWithCustomContext: onClickRight } = useOnClickFromAction(
        onRightValue,
        ActionSource.Tinder,
        ctx,
        env
    );
    const onLine = useNetworkStatus();
    if (listValue === LOADING || contents === LOADING) return env.renderProgress();
    if (onLeftValue === LOADING || onRightValue === LOADING) return env.renderProgress();
    if (listValue === undefined || contents === undefined) return null;

    const items = deconstructItems(listValue, force);
    if (items.length === 0) {
        if (rowToSetVP !== undefined) {
            rowToSetVP.set(undefined);
        }

        const emptyMessage = loadedDefinedMap(emptyMessageValue, asString);
        return <EmptyWrapper>{emptyMessage}</EmptyWrapper>;
    }

    let topTwo: [Item | undefined, Item | undefined] = [undefined, undefined];

    for (const i of items) {
        if (topTwo[0] === undefined || i.swipedTimestamp < topTwo[0].swipedTimestamp) {
            topTwo = [i, topTwo[0]];
        } else if (topTwo[1] === undefined || i.swipedTimestamp < topTwo[1].swipedTimestamp) {
            topTwo = [topTwo[0], i];
        }
    }

    const [maybeFirst, second] = topTwo;
    const first = defined(maybeFirst);
    const isEnabled = onLine && first.isEnabled && first.swipedTimestampVP !== undefined;

    const swipe = (handler: OnClickHandlerWithCustomContext) => {
        defined(first.swipedTimestampVP).set(getNowTimeZoneAgnostic());
        if (handler !== undefined) {
            dontAwait(handler(first.data, undefined));
        }
    };

    const frontContents = asUI(contents).render(first.data, env);
    const rearContents =
        second !== undefined ? asUI(contents).render(second.data, env.with({ omitOverlays: true })) : frontContents;

    if (rowToSetVP !== undefined) {
        const firstDataValue = force(first.data);
        // FIXME: If the row is loading, we'll display it as not present instead.
        // We could do a shitty hack and set it to the string "Loading" or something,
        // or instead use a resettable forwarding value producer or something.
        rowToSetVP.set(firstDataValue === LOADING ? undefined : firstDataValue);
    }

    return (
        <SwipeStack
            frontKey={first.key}
            rearKey={second?.key ?? first.key}
            onLeft={() => swipe(onClickLeft)}
            onRight={() => swipe(onClickRight)}
            frontContents={frontContents}
            rearContents={rearContents}
            isEnabled={isEnabled}
        />
    );
};
